import { type Order } from '../orders/orders-api';
import { hash, money } from '../orders/order-model';
import { bookingIcs, initialsOf } from '../store/store-model';
import {
  type Appointment,
  type Consultation,
  type ConsultationAnswer,
  type Listing,
  type PaymentState,
} from './bookings-api';

/**
 * The Bookings page's decisions, made once and pinned by tests.
 *
 * Every word here comes from a recorded value: the booking's own status and
 * payment state, the consultation's status, decision and messages, the order
 * a consultation was paid on. Nothing is guessed from the clock beyond what
 * the clock can honestly say ("starts in 3 hours"), and nothing from the
 * frame's demo (no "within 2 hours", no late-cancellation fee, no 24-hour
 * free-cancellation window, no "Before you go" list the pharmacy never wrote).
 */

export interface Tone {
  readonly bg: string;
  readonly fg: string;
}

const VIOLET: Tone = { bg: '#f0ecfa', fg: '#4a22bd' };
const AMBER: Tone = { bg: '#fdf2e4', fg: '#8c4a06' };
const GREEN: Tone = { bg: '#ddf2e7', fg: '#0b5a4a' };
const RED: Tone = { bg: '#fbe3e3', fg: '#8c1919' };
const TEAL: Tone = { bg: '#e7f6fa', fg: '#0d5d73' };
const GREY: Tone = { bg: '#f7f5fc', fg: '#55506b' };

const TILES = [
  'linear-gradient(145deg,#6d3ffb,#4a22bd)',
  'linear-gradient(145deg,#1bafd6,#0d7d9c)',
  'linear-gradient(145deg,#1f9d6b,#0e6f5c)',
  'linear-gradient(145deg,#f0738f,#c0396a)',
] as const;

export function shopTile(storefrontId: string, name: string): { initials: string; tile: string } {
  return {
    initials: initialsOf(name),
    tile: TILES[hash(storefrontId) % TILES.length] ?? TILES[0],
  };
}

// -- Clocks: the shop's own, which is the one that matters --------------------------

function fmt(iso: string, zone: string | undefined, options: Intl.DateTimeFormatOptions): string {
  const date = new Date(iso);
  try {
    return new Intl.DateTimeFormat('en-GB', {
      ...options,
      ...(zone !== undefined ? { timeZone: zone } : {}),
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  }
}

export const monthOf = (iso: string, zone?: string): string =>
  fmt(iso, zone, { month: 'short' }).toUpperCase();
export const dayOf = (iso: string, zone?: string): string => fmt(iso, zone, { day: 'numeric' });
export const clockOf = (iso: string, zone?: string): string =>
  fmt(iso, zone, { hour: '2-digit', minute: '2-digit', hour12: false });
/** "Wed 4 Nov" */
export const shortDay = (iso: string, zone?: string): string =>
  fmt(iso, zone, { weekday: 'short', day: 'numeric', month: 'short' });
/** "Wednesday 4 November" */
export const longDay = (iso: string, zone?: string): string =>
  fmt(iso, zone, { weekday: 'long', day: 'numeric', month: 'long' });
/** "1 Nov, 18:08" */
export const stamp = (iso: string, zone?: string): string =>
  `${fmt(iso, zone, { day: 'numeric', month: 'short' })}, ${clockOf(iso, zone)}`;

export function minutesOf(appointment: Pick<Appointment, 'startsAt' | 'endsAt'>): number {
  return Math.max(
    0,
    Math.round((Date.parse(appointment.endsAt) - Date.parse(appointment.startsAt)) / 60_000),
  );
}

// -- Appointments ---------------------------------------------------------------------

/** Still to come: pending or confirmed, and not over yet — the Account page's own rule. */
export function isUpcoming(appointment: Appointment, now = Date.now()): boolean {
  return (
    (appointment.status === 'PENDING' || appointment.status === 'CONFIRMED') &&
    Date.parse(appointment.endsAt) > now
  );
}

/** The platform lets a customer cancel PENDING or CONFIRMED; the page also hides it once the time has passed. */
export function canCancel(appointment: Appointment, now = Date.now()): boolean {
  return isUpcoming(appointment, now);
}

export const isVirtual = (appointment: Pick<Appointment, 'mode'>): boolean => appointment.mode === 'VIRTUAL';

export function appointmentBadge(appointment: Appointment, now = Date.now()): { label: string } & Tone {
  switch (appointment.status) {
    case 'CANCELLED':
      return { label: 'CANCELLED', ...RED };
    case 'COMPLETED':
      return { label: 'COMPLETED', ...GREEN };
    case 'NO_SHOW':
      return { label: 'MISSED', ...GREY };
    case 'PENDING':
      // A new booking is a request until the shop accepts it. Never "booked".
      return isUpcoming(appointment, now)
        ? { label: 'REQUESTED', ...AMBER }
        : { label: 'NOT CONFIRMED', ...GREY };
    case 'CONFIRMED': {
      if (!isUpcoming(appointment, now)) return { label: 'PAST', ...GREY };
      const today =
        clockDay(appointment.startsAt, appointment.timeZone) ===
        clockDay(new Date(now).toISOString(), appointment.timeZone);
      return today ? { label: 'TODAY', ...AMBER } : { label: 'BOOKED', ...VIOLET };
    }
    default:
      return { label: String(appointment.status), ...GREY };
  }
}

function clockDay(iso: string, zone: string): string {
  return fmt(iso, zone, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export type StripIcon = 'clock' | 'check' | 'x' | 'chat' | 'card';

export interface Strip extends Tone {
  readonly text: string;
  readonly icon: StripIcon;
}

export function appointmentStrip(
  appointment: Appointment,
  storeName: string,
  now = Date.now(),
): Strip | undefined {
  if (!isUpcoming(appointment, now)) return undefined;
  if (appointment.status === 'PENDING') {
    if (appointment.paymentState === 'REQUIRED')
      return { text: `Requested. Payment is due before ${storeName} can confirm it`, icon: 'card', ...AMBER };
    return { text: `Requested. Waiting for ${storeName} to accept`, icon: 'clock', ...AMBER };
  }
  const hours = (Date.parse(appointment.startsAt) - now) / 3_600_000;
  if (hours <= 0) return { text: 'Happening now', icon: 'clock', ...VIOLET };
  if (hours < 24) {
    const whole = Math.max(1, Math.round(hours));
    return { text: `Starts in ${String(whole)} ${whole === 1 ? 'hour' : 'hours'}`, icon: 'clock', ...VIOLET };
  }
  return { text: `Confirmed by ${storeName}`, icon: 'check', ...VIOLET };
}

/** The booking's own payment state, in words. */
export function appointmentPayment(state: PaymentState, priceMinor: number | undefined): string {
  if (state === 'PAID') return 'Paid';
  if (state === 'REQUIRED') return 'Payment due';
  if (priceMinor === 0) return 'No charge';
  return 'Not taken online';
}

export function cancelledNote(appointment: Appointment): string {
  if (appointment.paymentState === 'PAID')
    return 'You paid for it online. Returning the money is the pharmacy’s to do.';
  return 'Nothing was taken online for it.';
}

export function cancelCopy(appointment: Appointment, storeName: string): string {
  const money =
    appointment.paymentState === 'PAID'
      ? 'You paid online: returning the money is the pharmacy’s to do.'
      : 'Nothing has been taken online, so there’s nothing to refund.';
  return `${storeName} gets the time back. ${money}`;
}

export function listingPrice(listing: Listing | undefined): { label: string; minor?: number } {
  if (listing === undefined || !listing.price.resolved || listing.price.amountMinor === undefined)
    return { label: 'Price at the pharmacy' };
  const minor = listing.price.amountMinor;
  return { label: minor === 0 ? 'Free' : money(minor, listing.price.currency ?? 'GBP'), minor };
}

export function placeOf(listing: Listing | undefined, branchId?: string): string | undefined {
  if (listing === undefined) return undefined;
  const location =
    listing.locations.find((l) => l.id === branchId && (l.town !== undefined || l.postcode !== undefined)) ??
    listing.locations.find((l) => l.town !== undefined || l.postcode !== undefined);
  if (location === undefined) return undefined;
  const parts = [location.town, location.postcode].filter((p): p is string => p !== undefined && p !== '');
  return parts.length > 0 ? parts.join(', ') : undefined;
}

export function directionsUrl(storeName: string, place: string | undefined): string | undefined {
  if (place === undefined) return undefined;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${storeName} ${place}`)}`;
}

/** The calendar file: the booking's own times and status, never more. */
export function appointmentIcs(
  appointment: Appointment,
  title: string,
  storeName: string,
  where: string,
): string {
  return bookingIcs({
    id: appointment.id,
    title: `${title} at ${storeName}`,
    where,
    startsAt: appointment.startsAt,
    endsAt: appointment.endsAt,
    description:
      appointment.status === 'CONFIRMED'
        ? `Confirmed by ${storeName}. Booked through Just Treated.`
        : `Requested through Just Treated. Waiting for ${storeName} to accept.`,
  });
}

// -- Consultations ---------------------------------------------------------------------

/** Still in progress: answered, on an order, or waiting for the customer's reply. */
export function consultationOpen(consultation: Consultation, now = Date.now()): boolean {
  if (consultation.status === 'SUBMITTED') return Date.parse(consultation.expiresAt) > now;
  return consultation.status === 'IN_ORDER' || consultation.status === 'MORE_INFO';
}

/** SUBMITTED past its window is expired whether or not the server has said so yet. */
export function consultationState(consultation: Consultation, now = Date.now()): Consultation['status'] {
  if (consultation.status === 'SUBMITTED' && Date.parse(consultation.expiresAt) <= now) return 'EXPIRED';
  return consultation.status;
}

export function consultationBadge(consultation: Consultation, now = Date.now()): { label: string } & Tone {
  switch (consultationState(consultation, now)) {
    case 'SUBMITTED':
      return { label: 'SUBMITTED', ...VIOLET };
    case 'IN_ORDER':
      return { label: 'UNDER REVIEW', ...TEAL };
    case 'MORE_INFO':
      return { label: 'MORE INFO NEEDED', ...AMBER };
    case 'APPROVED':
      return { label: 'APPROVED', ...GREEN };
    case 'DECLINED':
      return { label: 'NOT APPROVED', ...RED };
    default:
      return { label: 'EXPIRED', ...GREY };
  }
}

export const isPaid = (order: Order | undefined): boolean => order?.payment.status === 'PAID';

/**
 * The card's strip. Owner decision D9: the platform's order is pay first, then
 * the prescriber reviews — so this never says "approved, now pay".
 */
export function consultationStrip(
  consultation: Consultation,
  order: Order | undefined,
  storeName: string,
  now = Date.now(),
): Strip {
  switch (consultationState(consultation, now)) {
    case 'SUBMITTED':
      return {
        text: 'Pay at checkout. A prescriber reviews your answers after you pay',
        icon: 'card',
        ...VIOLET,
      };
    case 'IN_ORDER':
      return isPaid(order)
        ? { text: 'Paid. A prescriber is reviewing your answers', icon: 'clock', ...TEAL }
        : { text: 'Waiting for payment, then a prescriber reviews it', icon: 'card', ...TEAL };
    case 'MORE_INFO':
      return { text: 'Your prescriber has a question for you', icon: 'chat', ...AMBER };
    case 'APPROVED':
      return { text: `Approved. The order goes to ${storeName}`, icon: 'check', ...GREEN };
    case 'DECLINED':
      return {
        text:
          order?.payment.status === 'REFUNDED'
            ? 'Not approved. The order was cancelled and refunded'
            : 'Not approved. The order is cancelled',
        icon: 'x',
        ...RED,
      };
    default:
      return { text: 'These answers expired before an order was paid', icon: 'x', ...GREY };
  }
}

export function consultationPayment(consultation: Consultation, order: Order | undefined): string {
  if (order !== undefined) {
    switch (order.payment.status) {
      case 'PAID':
        return 'Paid';
      case 'REFUNDED':
        return 'Refunded';
      case 'FAILED':
        return 'Payment failed';
      default:
        return 'Waiting on payment';
    }
  }
  return consultation.orderId !== undefined ? 'On an order' : 'Not paid yet';
}

/** The latest thing the prescriber said, for the note box. */
export function prescriberNote(
  consultation: Consultation,
  storeName: string,
  now = Date.now(),
): ({ head: string; text: string } & Tone) | undefined {
  const state = consultationState(consultation, now);
  if (state === 'DECLINED') {
    return {
      head: 'Not suitable this time',
      text: consultation.decision?.reason ?? 'The prescriber did not approve this treatment.',
      ...RED,
    };
  }
  if (state === 'APPROVED') {
    return {
      head: 'Your prescriber approved this',
      text: `${
        consultation.decision !== undefined ? `Approved on ${stamp(consultation.decision.at)}.` : 'Approved.'
      } ${storeName} dispenses it on your order.`,
      ...GREEN,
    };
  }
  const asked = [...consultation.messages].reverse().find((message) => message.from === 'PRESCRIBER');
  if (state === 'MORE_INFO' && asked !== undefined) {
    return { head: 'Message from your prescriber', text: asked.text, ...AMBER };
  }
  return undefined;
}

export interface ConsultStep {
  readonly label: string;
  readonly sub: string;
  readonly state: 'done' | 'current' | 'todo' | 'bad';
}

/** D9: Submitted → Paid → Prescriber review → Decision, each from a recorded time. */
export function consultationSteps(
  consultation: Consultation,
  order: Order | undefined,
  now = Date.now(),
): ConsultStep[] {
  const state = consultationState(consultation, now);
  const paid =
    isPaid(order) || order?.payment.status === 'REFUNDED' || state === 'APPROVED' || state === 'DECLINED';
  const decided = consultation.decision;
  const paidAt = order?.history.find((h) => h.status === 'CONFIRMED')?.at;
  return [
    { label: 'Answers submitted', sub: stamp(consultation.submittedAt), state: 'done' },
    state === 'EXPIRED'
      ? { label: 'Paid', sub: 'Expired before payment', state: 'bad' }
      : paid
        ? { label: 'Paid', sub: paidAt !== undefined ? stamp(paidAt) : 'Paid at checkout', state: 'done' }
        : {
            label: 'Paid',
            sub: consultation.orderId !== undefined ? 'Waiting on payment' : 'You pay at checkout',
            state: 'current',
          },
    decided !== undefined
      ? { label: 'Prescriber review', sub: 'Reviewed', state: 'done' }
      : state === 'MORE_INFO'
        ? { label: 'Prescriber review', sub: 'Waiting for your reply', state: 'current' }
        : paid
          ? { label: 'Prescriber review', sub: 'In review', state: 'current' }
          : { label: 'Prescriber review', sub: 'After you pay', state: 'todo' },
    decided !== undefined
      ? {
          label: 'Decision',
          sub: `${decided.outcome === 'APPROVED' ? 'Approved' : 'Not approved'} · ${stamp(decided.at)}`,
          state: decided.outcome === 'APPROVED' ? 'done' : 'bad',
        }
      : { label: 'Decision', sub: 'Shown here when it is made', state: 'todo' },
  ];
}

/** One answer, as the customer gave it. */
export function answerText(answer: ConsultationAnswer): string {
  if (answer.labels !== undefined && answer.labels.length > 0) return answer.labels.join(', ');
  const value = answer.value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value === '' ? '—' : value;
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    if (typeof record.value === 'number' && typeof record.unit === 'string')
      return `${String(record.value)} ${record.unit}`;
    if (typeof record.name === 'string') return record.name;
    if (Array.isArray(record.documentIds)) {
      const n = record.documentIds.length;
      return `${String(n)} ${n === 1 ? 'file' : 'files'} sent`;
    }
  }
  return '—';
}
